/**
 * Telemetry noise filter.
 *
 * The preview and dev bundles produce console output that says nothing about
 * production health, and unfiltered it drowns the real signal in PostHog — it
 * was around 70% of everything ingested. Pure predicates, so they can be tested
 * without a DOM.
 */

const NOISE_PATTERNS: readonly string[] = [
  // Host-browser extraction scripts can print cookies and page contents.
  "[video-takeover]",
  "createAdvanceExtractConfigs:",
  // React development-only warnings (never emitted by a production build)
  "Warning: Function components cannot be given refs",
  "Warning: Missing `Description` or `aria-describedby",
  "Warning: validateDOMNesting",
  "Warning: Each child in a list should have a unique",
  "Warning: React does not recognize the",
  "Warning: findDOMNode is deprecated",
  "Download the React DevTools",
  // Vite dev server / HMR / build pipeline
  "[hmr]",
  "[vite]",
  "hot updated",
  "Failed to reload /src/",
  "server connection lost",
  // Router upgrade notices
  "React Router Future Flag Warning",
  // Lovable preview harness postMessage bridge
  "Unknown message type:",
  "RESET_BLANK_CHECK",
  "SET_SAFE_AREA",
  // MediaPipe / TFLite native stderr
  "gl_context.cc",
  "OpenGL error checking is disabled",
  "INFO: Created TensorFlow Lite",
  "TensorFlow Lite XNNPACK delegate",
];

/** True when a console message is build/dev noise that must not reach PostHog. */
export function isNoisyConsoleMessage(message: string): boolean {
  if (!message) return true;
  return NOISE_PATTERNS.some((pattern) => message.includes(pattern));
}

/** True when an error comes from tooling — Vite, HMR, injected preview scripts. */
export function isNoisyErrorSource(source: string | undefined | null): boolean {
  if (!source) return false;
  return (
    source.includes("/@vite/") ||
    source.includes("/@react-refresh") ||
    // Vite's optimized dependency cache, e.g. `/node_modules/.vite/deps/…`,
    // ships only from the dev server; a crash inside it is never production.
    source.includes(".vite/deps") ||
    source.includes("__lovable") ||
    source.includes("chrome-extension://")
  );
}

/**
 * True when a stacktrace has frames and every one names a tooling source.
 *
 * Exception autocapture files a `$exception` without running the source filter
 * above, so `before_send` reuses this to drop an autocaptured exception whose
 * whole trace sits in the dev server. An empty stack is not dev-only — it can
 * be a real report whose frames were stripped.
 */
export function isDevServerStack(
  sources: readonly (string | undefined)[],
): boolean {
  if (sources.length === 0) return false;
  return sources.every((source) => isNoisyErrorSource(source));
}

/**
 * An Android in-app browser injects a JavaScript bridge so the page can talk to
 * its host app. When the WebView releases that Java object while the page still
 * calls it — which happens as the page tears down — the bridge throws
 * "… Java object is gone", and exception autocapture files it at severity high.
 * The throw belongs to the in-app browser, not to this application: the visitor
 * was already leaving, and no frame names a file in `src/`. A variant carries a
 * stack whose frames are the bridge's own sender functions; the first report
 * carried only the message. Either signature is enough.
 *
 * The same host injects an autofill routine that reaches for its own global.
 * When the browser has not installed that global — a different tab, a page it
 * decided not to bridge — the routine throws `xbrowser is not defined` from a
 * frame with no file behind it. Nothing in this application is on that stack
 * either, and no form here is one the host could fill.
 *
 * iOS in-app WebViews do the same thing through `window.webkit.messageHandlers`,
 * and a script that assumes the bridge is installed throws when it is not. The
 * word `webkit` appears nowhere in `src/` — the message is the whole evidence
 * needed, as it is for the autofill global above.
 */
const NATIVE_BRIDGE_MESSAGES: readonly string[] = [
  // Android WebView's message when an injected `@JavascriptInterface` object is
  // used after the WebView released it.
  "Java object is gone",
  // The X Browser autofill script's missing host global.
  "xbrowser is not defined",
  // An injected iOS bridge reaching for a `window.webkit` that is not there.
  // WebKit words it as a property read on undefined and quotes the expression,
  // so the expression is what matches rather than the whole sentence.
  "window.webkit.messageHandlers",
];

const NATIVE_BRIDGE_FRAME_FUNCTIONS: readonly string[] = [
  "sendDataToNative",
  "sendBeforeUnloadMessage",
  // The injected autofill entry point; its frame names no file.
  "execute_auto_fill",
];

/**
 * True when an autocaptured exception comes from a native bridge an in-app
 * browser injects, by its disposal message or by its own frame functions.
 */
export function isInjectedNativeBridgeException(
  messages: readonly (string | undefined)[],
  frameFunctions: readonly (string | undefined)[],
): boolean {
  return (
    messages.some(
      (message) =>
        !!message &&
        NATIVE_BRIDGE_MESSAGES.some((pattern) => message.includes(pattern)),
    ) ||
    frameFunctions.some(
      (fn) => !!fn && NATIVE_BRIDGE_FRAME_FUNCTIONS.includes(fn),
    )
  );
}

/**
 * The analytics SDK's session recorder builds its worker from an object URL and
 * revokes that URL in the `finally` of the same statement, while the browser is
 * still fetching the script that URL names. When the revoke wins the race the
 * load fails, and exception autocapture files the recorder's own defect as this
 * application's.
 *
 * The workers this application constructs catch their own failures and report
 * them under their own messages — `src/test/noOwnWorkers.test.ts` lists them
 * and keeps that true — so a worker script failure that reaches autocapture is
 * never one of ours, and the message alone is enough to say so.
 * The stack under it is a minified third-party bundle and names nothing worth
 * matching on.
 */
const FOREIGN_WORKER_MESSAGES: readonly string[] = [
  // WebKit names the script it could not load.
  "Failed to load worker script at",
  // Blink words the same failure as a constructor that could not run.
  "Failed to construct 'Worker'",
  "Failed to construct 'SharedWorker'",
];

/**
 * True when an autocaptured exception reports a worker script that would not
 * load, which by the premise above belongs to somebody else's code.
 */
export function isForeignWorkerScriptException(
  messages: readonly (string | undefined)[],
): boolean {
  return messages.some(
    (message) =>
      !!message &&
      FOREIGN_WORKER_MESSAGES.some((pattern) => message.includes(pattern)),
  );
}

/**
 * Decides whether a console entry should be forwarded to analytics.
 *
 * A development build forwards nothing: dev warnings and build errors are for
 * the terminal, and that single check is what removes most of the noise. `log`
 * and `info` *are* forwarded from production, because they are almost the
 * entire account the application gives of itself — every `[room] …` line, every
 * channel status, every peer connection. Volume is held down by the pattern
 * list above and the per-minute budget in `analytics.ts`, not by banning the
 * two levels that carry the story.
 */
export function shouldForwardConsole(
  level: "log" | "info" | "warn" | "error",
  message: string,
  isDevBuild: boolean,
): boolean {
  if (isDevBuild) return false;
  return !isNoisyConsoleMessage(message);
}

/**
 * Hot-reload artefacts. While a module is edited in the preview React re-renders
 * against a half-swapped module graph, and the resulting burst of
 * ReferenceErrors and React invariants must never enter Error tracking.
 */
const DEV_ONLY_ERROR_PATTERNS: readonly RegExp[] = [
  /\bis not defined\b/,
  /Cannot access '.*' before initialization/,
  /Should have a queue/,
  /Rendered (more|fewer) hooks than/,
  /reading 'getSnapshot'/,
  // React's hook dispatcher is briefly null while Vite swaps `react` and
  // `react-dom` in separate optimize passes; the next render reads a hook off
  // it and throws `…null (reading 'useRef'|'useState'|…)`.
  /Cannot read properties of null \(reading 'use[A-Z]\w*'\)/,
  /null is not an object \(evaluating '\w+\.use[A-Z]\w*'\)/,
  /Failed to fetch dynamically imported module/,
  /error loading dynamically imported module/,
];

/**
 * Two classes are dropped: development-build hot-reload artefacts, and opaque
 * cross-origin `Script error.` reports, which carry nothing to act on.
 */
export function shouldForwardRuntimeError(
  message: string,
  stack: string | undefined | null,
  isDevBuild: boolean,
): boolean {
  const text = message ?? "";
  if (!stack && /^script error\.?$/i.test(text.trim())) return false;
  if (
    isDevBuild &&
    DEV_ONLY_ERROR_PATTERNS.some((pattern) => pattern.test(text))
  )
    return false;
  return true;
}
